import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { apiUrl } from '../config/api';
import { CHUNK_ADVANCE_BUFFER_SECONDS } from '../config/playback';

/**
 * Audio element control, seek, skip, speed, chunk switching.
 * Owns currentTime, isPlaying, currentChunkIndex, playbackSpeed.
 * Derives chapter-relative state from manifest + currentTime.
 */
export function usePlayback(audioRef, manifestData) {
  const {
    manifest,
    visibleChapters,
    firstVisibleChapter,
    allWords,
    initialSeekDoneRef,
  } = manifestData;

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState("1");
  const [isBuffering, setIsBuffering] = useState(false);
  const seekingRef = useRef(false);
  const chunkCacheRef = useRef({}); // { [chunkIndex]: blobUrl }

  // ============================================================
  // Current chapter index (derived from currentTime)
  // ============================================================

  const currentChapterIndex = useMemo(() => {
    if (visibleChapters.length === 0) return -1;
    let idx = 0;
    for (let i = 0; i < visibleChapters.length; i++) {
      if (currentTime >= visibleChapters[i].startTime) idx = i;
      else break;
    }
    return idx;
  }, [visibleChapters, currentTime]);

  const currentChapter = visibleChapters[currentChapterIndex] || null;

  // ============================================================
  // Words for the current chapter only
  // ============================================================

  const currentChapterWords = useMemo(() => {
    if (!currentChapter || allWords.length === 0 || !manifest?.chapters) return [];
    const startWordIdx = currentChapter.wordIndex;
    const fullIdx = manifest.chapters.findIndex(c => c.wordIndex === currentChapter.wordIndex);
    const nextChapter = fullIdx >= 0 && fullIdx + 1 < manifest.chapters.length
      ? manifest.chapters[fullIdx + 1]
      : null;
    const endWordIdx = nextChapter ? nextChapter.wordIndex : allWords.length;
    return allWords.slice(startWordIdx, endWordIdx);
  }, [currentChapter, manifest, allWords]);

  // Chapter time bounds
  const chapterStartTime = currentChapter?.startTime || 0;
  const chapterEndTime = useMemo(() => {
    if (!currentChapter || !manifest?.chapters) return manifest?.totalDuration || 0;
    const fullIdx = manifest.chapters.findIndex(c => c.wordIndex === currentChapter.wordIndex);
    const nextChapter = fullIdx >= 0 && fullIdx + 1 < manifest.chapters.length
      ? manifest.chapters[fullIdx + 1]
      : null;
    return nextChapter ? nextChapter.startTime : (manifest?.totalDuration || 0);
  }, [currentChapter, manifest]);
  const chapterDuration = chapterEndTime - chapterStartTime;
  const chapterRelativeTime = Math.max(0, currentTime - chapterStartTime);

  // ============================================================
  // Chunk helpers
  // ============================================================

  const getChunkStartTime = useCallback((chunkIndex) => {
    if (!manifest?.chunks) return 0;
    let total = 0;
    for (let i = 0; i < chunkIndex && i < manifest.chunks.length; i++) {
      total += manifest.chunks[i].duration;
    }
    return total;
  }, [manifest]);

  const findChunkForTime = useCallback((globalTime) => {
    if (!manifest?.chunks) return { chunkIndex: 0, localTime: 0 };
    let elapsed = 0;
    for (let i = 0; i < manifest.chunks.length; i++) {
      const dur = manifest.chunks[i].duration;
      if (globalTime < elapsed + dur) {
        return { chunkIndex: i, localTime: globalTime - elapsed };
      }
      elapsed += dur;
    }
    const lastIdx = manifest.chunks.length - 1;
    return { chunkIndex: lastIdx, localTime: manifest.chunks[lastIdx].duration };
  }, [manifest]);

  // ============================================================
  // Chunk preloading — fetch audio into blob cache for instant seeking
  // ============================================================

  const preloadChunk = useCallback((chunkIndex) => {
    if (!manifest?.chunks || chunkCacheRef.current[chunkIndex]) return;
    const chunk = manifest.chunks[chunkIndex];
    if (!chunk) return;
    const url = apiUrl(chunk.audioUrl);
    fetch(url)
      .then(res => res.blob())
      .then(blob => {
        chunkCacheRef.current[chunkIndex] = URL.createObjectURL(blob);
      })
      .catch(() => {}); // Silent fail — will fall back to direct URL
  }, [manifest]);

  // Chapter-scope preload: when a chapter becomes current, warm every chunk
  // in that chapter + the next chapter into the blob cache. Good for seeks.
  useEffect(() => {
    if (!manifest?.chunks || currentChapterIndex < 0) return;

    const chaptersToPreload = [currentChapterIndex, currentChapterIndex + 1]
      .filter(i => i < visibleChapters.length);

    let cumulativeWords = 0;
    for (const chunk of manifest.chunks) {
      const chunkEndWord = cumulativeWords + (chunk.words?.length || 0);
      for (const ci of chaptersToPreload) {
        const ch = visibleChapters[ci];
        const nextCh = ci + 1 < visibleChapters.length ? visibleChapters[ci + 1] : null;
        const chStart = ch?.wordIndex || 0;
        const chEnd = nextCh?.wordIndex || Infinity;
        if (cumulativeWords < chEnd && chunkEndWord > chStart) {
          preloadChunk(chunk.index);
        }
      }
      cumulativeWords = chunkEndWord;
    }
  }, [manifest, currentChapterIndex, visibleChapters, preloadChunk]);

  // Just-in-time lookahead preload. Every time the playing chunk advances,
  // make sure the NEXT two chunks are warm — so a chunk boundary never
  // stalls audio waiting on a fetch, even right at the start of a book
  // before the chapter-scope preload has caught up. preloadChunk is
  // idempotent (bails if already cached), so double-triggering with the
  // chapter-scope preload is free.
  useEffect(() => {
    if (!manifest?.chunks) return;
    for (let offset = 1; offset <= 2; offset++) {
      const target = currentChunkIndex + offset;
      if (target < manifest.chunks.length) preloadChunk(target);
    }
  }, [currentChunkIndex, manifest, preloadChunk]);

  // ============================================================
  // Seek to global time (cross-chunk)
  // ============================================================

  const seekToGlobalTime = useCallback((globalTime) => {
    if (!manifest?.chunks || !audioRef.current) return;

    const audio = audioRef.current;
    const { chunkIndex, localTime } = findChunkForTime(globalTime);
    const newChunk = manifest.chunks[chunkIndex];
    const expectedSrc = apiUrl(newChunk.audioUrl);

    // Compare chunk index only — audioUrl has cache-bust params (?v=timestamp)
    // that differ between manifest fetches, so URL comparison is unreliable.
    const needsLoad = chunkIndex !== currentChunkIndex || !audio.src;

    if (needsLoad) {
      seekingRef.current = true;
      setIsBuffering(true);
      const wasPlaying = !audio.paused && !audio.ended && audio.readyState > 0;
      audio.pause();

      // Use cached blob URL if available (instant), otherwise fetch from server
      const cachedUrl = chunkCacheRef.current[chunkIndex];
      audio.src = cachedUrl || expectedSrc;
      audio.load();

      const onCanPlay = () => {
        try {
          audio.currentTime = localTime;
        } catch (e) {
          console.error('[usePlayback] Failed to set currentTime after load:', e);
        }
        seekingRef.current = false;
        setIsBuffering(false);
        if (wasPlaying) {
          audio.play().catch(err => console.error('[usePlayback] Play after seek failed:', err));
        }
        audio.removeEventListener('canplay', onCanPlay);
      };
      audio.addEventListener('canplay', onCanPlay);

      if (chunkIndex !== currentChunkIndex) {
        setCurrentChunkIndex(chunkIndex);
      }
    } else {
      // Same chunk — pause briefly, seek, resume. Without pausing, the RAF loop
      // reads the old audio.currentTime before the seek completes and snaps back.
      const wasPlaying = !audio.paused;
      if (wasPlaying) audio.pause();
      audio.currentTime = localTime;
      seekingRef.current = true;
      const onSeeked = () => {
        seekingRef.current = false;
        if (wasPlaying) audio.play().catch(() => {});
        audio.removeEventListener('seeked', onSeeked);
      };
      audio.addEventListener('seeked', onSeeked);
    }

    setCurrentTime(globalTime);
  }, [manifest, currentChunkIndex, findChunkForTime, audioRef]);

  const seekToChapter = useCallback((visibleChapterIdx) => {
    const ch = visibleChapters[visibleChapterIdx];
    if (ch) seekToGlobalTime(ch.startTime);
  }, [visibleChapters, seekToGlobalTime]);

  // ============================================================
  // Auto-load first chunk + auto-seek to first visible chapter
  // ============================================================

  useEffect(() => {
    if (!manifest?.chunks?.length || !audioRef.current) return;
    if (initialSeekDoneRef.current) return;

    if (firstVisibleChapter && firstVisibleChapter.startTime > 0) {
      seekToGlobalTime(firstVisibleChapter.startTime);
    } else {
      const chunk0 = manifest.chunks[0];
      audioRef.current.src = apiUrl(chunk0.audioUrl);
      audioRef.current.load();
      setCurrentChunkIndex(0);
    }
    initialSeekDoneRef.current = true;
  }, [manifest, firstVisibleChapter, seekToGlobalTime, audioRef, initialSeekDoneRef]);

  // ============================================================
  // Auto-advance to next chunk when current finishes
  // ============================================================

  useEffect(() => {
    if (!audioRef.current || !manifest?.chunks) return;

    const audio = audioRef.current;
    const currentChunk = manifest.chunks[currentChunkIndex];
    if (!currentChunk) return;

    // Chunk-advance logic. Two triggers so we never get stuck:
    //   - timeupdate (every ~200ms) with a tiny buffer for reliability
    //   - ended event fires if MP3 runs out before timeupdate catches it
    // Whichever triggers first advances the chunk; the other becomes a no-op
    // (guarded by advanceInFlightRef).
    const advanceInFlightRef = { current: false };

    const advance = () => {
      if (advanceInFlightRef.current) return;
      if (currentChunkIndex >= manifest.chunks.length - 1) return;
      advanceInFlightRef.current = true;

      // `wasPlaying` must include audio.ended=true. When the 'ended' event
      // triggers advance (MP3 reached natural end), audio.paused becomes
      // true and audio.ended becomes true — checking only !audio.paused
      // here would skip audio.play() on the new chunk and freeze playback
      // at the chunk boundary. Audio that ended while playing was, by
      // definition, playing — so the user wants continuation.
      const wasPlaying = !audio.paused || audio.ended;
      const nextIndex = currentChunkIndex + 1;
      const nextChunk = manifest.chunks[nextIndex];

      // Prefer the preloaded blob URL — swap is instant. Fall back to the
      // API URL if the preload hasn't populated for this chunk yet.
      const cachedUrl = chunkCacheRef.current[nextIndex];
      audio.src = cachedUrl || apiUrl(nextChunk.audioUrl);
      audio.load();
      setCurrentChunkIndex(nextIndex);

      if (wasPlaying) {
        const onReady = () => {
          audio.play().catch(() => {});
          audio.removeEventListener('canplay', onReady);
        };
        audio.addEventListener('canplay', onReady);
      }
    };

    const handleTimeUpdate = () => {
      const localTime = audio.currentTime;
      const chunkDuration = currentChunk.duration;
      if (localTime >= chunkDuration - CHUNK_ADVANCE_BUFFER_SECONDS) advance();
    };

    const handleEnded = () => advance();

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [manifest, currentChunkIndex, audioRef]);

  // ============================================================
  // Playback controls
  // ============================================================

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      console.warn('[usePlayback] togglePlayPause: no audio element');
      return;
    }
    if (!audio.currentSrc && manifest?.chunks?.length) {
      const targetTime = (visibleChapters[0]?.startTime) || 0;
      seekToGlobalTime(targetTime);
      const onReady = () => {
        audio.play().catch(err => console.error('[usePlayback] Play after lazy-load failed:', err));
        audio.removeEventListener('canplay', onReady);
      };
      audio.addEventListener('canplay', onReady);
      return;
    }
    if (audio.paused) {
      audio.play().catch(err => {
        console.error('[usePlayback] Play failed:', err);
        setIsPlaying(false);
      });
    } else {
      audio.pause();
    }
  }, [manifest, visibleChapters, seekToGlobalTime, audioRef]);

  const skipForward = useCallback(() => {
    const target = Math.min(currentTime + 5, manifest?.totalDuration || 0);
    seekToGlobalTime(target);
  }, [currentTime, manifest, seekToGlobalTime]);

  const skipBackward = useCallback(() => {
    seekToGlobalTime(Math.max(currentTime - 5, 0));
  }, [currentTime, seekToGlobalTime]);

  const adjustSpeed = useCallback((newSpeed) => {
    setPlaybackSpeed(newSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = parseFloat(newSpeed);
    }
  }, [audioRef]);

  const resetPlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
    setCurrentTime(0);
    setCurrentChunkIndex(0);
    setIsPlaying(false);
  }, [audioRef]);

  // Audio element event handlers
  const handleAudioPlay = useCallback(() => setIsPlaying(true), []);
  const handleAudioPause = useCallback(() => setIsPlaying(false), []);
  const handleAudioEnded = useCallback(() => setIsPlaying(false), []);

  return {
    isPlaying,
    isBuffering,
    currentTime,
    setCurrentTime,
    currentChunkIndex,
    playbackSpeed,

    currentChapterIndex,
    currentChapter,
    currentChapterWords,
    chapterStartTime,
    chapterEndTime,
    chapterDuration,
    chapterRelativeTime,

    getChunkStartTime,
    seekingRef,
    seekToGlobalTime,
    seekToChapter,
    togglePlayPause,
    skipForward,
    skipBackward,
    adjustSpeed,
    resetPlayback,

    handleAudioPlay,
    handleAudioPause,
    handleAudioEnded,
  };
}
